import {
	type DataNeededId,
	Dtos,
	type ProConId,
	type SuretyAccountAttachmentFileType,
} from "@inrev/common";
import { type DataNeeded, proConsQueryKey, usePendingIds } from "@portal/api/decisioning";
import { useCreateDataNeeded, useUpdateDataNeeded } from "@portal/api/decisioning/admin";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { DropdownInput } from "../../../../form/inputs/DropdownInput";
import { Input } from "../../../../form/inputs/Input";
import {
	SearchableDropdownInput,
	type SearchableDropdownInputOption,
} from "../../../../form/inputs/SearchableDropdownInput";
import { TextAreaInput } from "../../../../form/inputs/TextAreaInput";
import { cn } from "../../../../lib/utils";
import { PanelSheet } from "../../../../patterns/panel-sheet";
import { ClickableText, Icon, SegmentedControls, Text } from "../../../../primitives";
import {
	DATA_NEEDED_IMPORTANCE_LABELS,
	DATA_NEEDED_IMPORTANCES,
	IMPORTANCE_DOT_CLASSES,
	toOptions,
} from "../../decisioningLabels";
import { useDecisioningData } from "../DecisioningDataContext";
import { type DecisioningSheet, useDecisioningPanel } from "../DecisioningPanelContext";
import { PanelField } from "../PanelField";
import {
	emptyRequestDraft,
	isRequestDraftChanged,
	REQUEST_TITLE_LABELS,
	type RequestDraft,
	requestDraftFromCreateInput,
	requestDraftFromItem,
	requestDraftIssue,
	toCreateInput,
	toUpdateChanges,
	withPickedFileTypes,
} from "./requestDraft";

type DataNeededType = Dtos.Decisioning.DataNeededType;
type RequestSheet = Extract<DecisioningSheet, { kind: "request" }>;

const NO_FACTOR = "__none__";
const ALREADY_REQUESTED = "Already requested";
const FILE_TYPE_EMPTY_MESSAGE =
	"No file types match. Pick the closest Other type and describe it in Description.";

const TYPE_SEGMENTS: readonly {
	type: DataNeededType;
	icon: "document-text" | "chat-bubble-oval-left" | "check-circle";
}[] = [
	{ type: "Document", icon: "document-text" },
	{ type: "Question", icon: "chat-bubble-oval-left" },
	{ type: "Task", icon: "check-circle" },
];

const ASSIGNEES = Dtos.Decisioning.dataNeededAssigneeSchema.options;

const FILE_TYPES: readonly SuretyAccountAttachmentFileType[] =
	Dtos.Decisioning.dataNeededFileTypeSections.flatMap((section) => section.fileTypes);

const SEGMENT_CLASSES = "flex-1 px-1.5 text-[13px]";

const RequestSheetForm: FC<{
	item?: DataNeeded;
	linkedProConId?: ProConId;
	restoredDraft?: RequestDraft;
}> = ({ item, linkedProConId, restoredDraft }) => {
	const { accountId, closeSheet, jumpTo, openSheet, toastOnSave } = useDecisioningPanel();
	const { dataNeeded, proCons, proConsById } = useDecisioningData();
	const pendingProConIds = usePendingIds(proConsQueryKey(accountId));
	const linkableProCons = useMemo(
		() => proCons.filter((proCon) => !pendingProConIds.has(proCon.id)),
		[proCons, pendingProConIds],
	);
	const { createDataNeededAsync } = useCreateDataNeeded(accountId, {
		onCreated: (created) => jumpTo({ tab: "data-needed", id: created.id }),
		failureAction: (input) => ({
			label: "Reopen",
			onClick: () =>
				openSheet({ kind: "request", mode: "add", draft: requestDraftFromCreateInput(input) }),
		}),
	});
	/** What the sheet held when Save was clicked; the sheet has closed by the time a save fails. */
	const savedDraft = useRef<RequestDraft | null>(null);
	const { updateDataNeededAsync } = useUpdateDataNeeded(accountId, {
		failureAction: ({ id }) => {
			const saved = savedDraft.current;
			if (!saved) return undefined;
			return {
				label: "Reopen",
				onClick: () => openSheet({ kind: "request", mode: "edit", dataNeededId: id, draft: saved }),
			};
		},
	});

	const [draft, setDraft] = useState<RequestDraft>(
		() =>
			restoredDraft ??
			(item
				? requestDraftFromItem(item)
				: emptyRequestDraft(
						linkedProConId !== undefined && !pendingProConIds.has(linkedProConId)
							? linkedProConId
							: null,
					)),
	);
	const [initialDraft] = useState(draft);
	const dirty = isRequestDraftChanged(draft, initialDraft);
	const patch = (changes: Partial<RequestDraft>) => setDraft((prev) => ({ ...prev, ...changes }));

	const pickFileTypes = (values: string[]) => {
		const picked = new Set(values);
		const fileTypes = FILE_TYPES.filter((fileType) => picked.has(fileType));
		setDraft((prev) => withPickedFileTypes(prev, fileTypes));
	};

	const editing = item !== undefined;
	const issue = requestDraftIssue(draft);
	const primaryFilled =
		draft.title.trim() !== "" && (draft.type !== "Document" || draft.fileTypes.length > 0);
	const rationaleMissing = primaryFilled && draft.rationale.trim() === "";
	const linkedFactor = draft.proConId ? proConsById.get(draft.proConId) : undefined;
	const createProConId = linkableProCons.find((proCon) => proCon.id === draft.proConId)?.id ?? null;
	const assigneeLabel = draft.assignee === "Agent" ? "the agent" : draft.assignee;

	const fileTypeOptions = useMemo<SearchableDropdownInputOption[]>(() => {
		const requested = new Set(
			dataNeeded.filter((open) => open.id !== item?.id).flatMap((open) => open.fileTypes),
		);
		return Dtos.Decisioning.dataNeededFileTypeSections.flatMap((section) =>
			section.fileTypes.map((fileType) => {
				const label = Dtos.Decisioning.fileTypeLabel(fileType);
				return {
					value: fileType,
					label,
					group: section.name,
					searchValues: [label, section.name],
					...(requested.has(fileType) && { disabled: true, note: ALREADY_REQUESTED }),
				};
			}),
		);
	}, [dataNeeded, item?.id]);

	const factorOptions = useMemo(
		() => [
			{ value: NO_FACTOR, label: "None" },
			...linkableProCons.map((proCon) => ({
				value: proCon.id,
				label: `${proCon.factor.name} (${proCon.kind})`,
			})),
		],
		[linkableProCons],
	);

	const status = (() => {
		if (issue) return { text: issue, tone: "danger" as const };
		if (linkedFactor)
			return { text: `Also shows on ${linkedFactor.factor.name}.`, tone: "default" as const };
		return { text: "Sorted into Data needed by importance.", tone: "default" as const };
	})();

	const save = () => {
		if (issue) return;
		if (item) {
			const changes = toUpdateChanges(draft, item);
			closeSheet();
			jumpTo({ tab: "data-needed", id: item.id });
			if (Object.keys(changes).length === 0) return;
			savedDraft.current = draft;
			toastOnSave(updateDataNeededAsync({ id: item.id, changes }), `Updated ${draft.title.trim()}`);
			return;
		}
		const title = draft.title.trim();
		closeSheet();
		toastOnSave(
			createDataNeededAsync(toCreateInput({ ...draft, proConId: createProConId })),
			draft.assignee === "Agent"
				? `Requested ${title}`
				: `Requested ${title} from ${draft.assignee}`,
		);
	};

	return (
		<PanelSheet
			title={editing ? "Edit request" : "Request data"}
			testId="request-data-sheet"
			status={status.text}
			statusTone={status.tone}
			onCancel={closeSheet}
			dirty={dirty}
			primaryLabel={editing ? "Save" : "Create request"}
			primaryDisabled={issue !== null}
			onPrimary={save}
		>
			<SegmentedControls
				value={draft.type}
				onChange={(type: DataNeededType) => patch({ type })}
				className="flex w-full"
				aria-label="Request type"
			>
				{TYPE_SEGMENTS.map(({ type, icon }) => (
					<SegmentedControls.Segment
						key={type}
						value={type}
						className={cn(SEGMENT_CLASSES, "gap-1.5")}
						data-testid={`request-type-${type}`}
					>
						<Icon type={icon} size="micro" aria-hidden />
						{type}
					</SegmentedControls.Segment>
				))}
			</SegmentedControls>

			{draft.type === "Document" ? (
				<>
					<PanelField label={REQUEST_TITLE_LABELS.Document} required testId="request-file-type">
						{({ id, labelId }) => (
							<SearchableDropdownInput
								multiple
								id={id}
								aria-labelledby={labelId}
								compact
								anchor="bottom start"
								value={draft.fileTypes}
								onChange={pickFileTypes}
								options={fileTypeOptions}
								placeholder="Select file types"
								searchPlaceholder="Search file types"
								emptyMessage={FILE_TYPE_EMPTY_MESSAGE}
							/>
						)}
					</PanelField>
					<PanelField label="Title" required>
						{({ id }) => (
							<Input
								id={id}
								compact
								value={draft.title}
								placeholder="e.g. Tom Smith's last bank statements"
								onChange={(event) => patch({ title: event.target.value })}
								testId="request-document-title"
							/>
						)}
					</PanelField>
				</>
			) : (
				<>
					<PanelField label={REQUEST_TITLE_LABELS[draft.type]} required testId="request-title">
						{({ id }) => (
							<Input
								id={id}
								compact
								value={draft.title}
								onChange={(event) => patch({ title: event.target.value })}
								testId="request-title-input"
							/>
						)}
					</PanelField>
				</>
			)}

			<PanelField label="Description">
				{({ id }) => (
					<TextAreaInput
						id={id}
						className="h-[64px]"
						value={draft.description}
						placeholder={`Shared with ${assigneeLabel}`}
						onChange={(event) => patch({ description: event.target.value ?? "" })}
						data-testid="request-description"
					/>
				)}
			</PanelField>

			<PanelField label="Rationale" required>
				{({ id }) => (
					<TextAreaInput
						id={id}
						className="h-[64px]"
						value={draft.rationale}
						placeholder="Why this is needed for the account"
						invalid={rationaleMissing}
						onChange={(event) => patch({ rationale: event.target.value ?? "" })}
						data-testid="request-rationale"
					/>
				)}
			</PanelField>

			<div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3.5">
				<PanelField label="Assigned to" required>
					{({ labelId }) => (
						<SegmentedControls
							value={draft.assignee}
							onChange={(assignee: Dtos.Decisioning.DataNeededAssignee) => patch({ assignee })}
							className="flex w-full"
							aria-labelledby={labelId}
						>
							{toOptions(ASSIGNEES).map((option) => (
								<SegmentedControls.Segment
									key={option.value}
									value={option.value}
									className={SEGMENT_CLASSES}
									data-testid={`request-assignee-${option.value}`}
								>
									{option.label}
								</SegmentedControls.Segment>
							))}
						</SegmentedControls>
					)}
				</PanelField>
				<PanelField label="Importance" required>
					{({ labelId }) => (
						<SegmentedControls
							value={draft.importance}
							onChange={(importance: Dtos.Decisioning.DataNeededImportance) =>
								patch({ importance })
							}
							className="flex w-full"
							aria-labelledby={labelId}
						>
							{DATA_NEEDED_IMPORTANCES.map((importance) => (
								<SegmentedControls.Segment
									key={importance}
									value={importance}
									className={cn(SEGMENT_CLASSES, "gap-1.5")}
									data-testid={`request-importance-${DATA_NEEDED_IMPORTANCE_LABELS[importance]}`}
								>
									<span
										aria-hidden
										className={cn("size-1.5 rounded-full", IMPORTANCE_DOT_CLASSES[importance])}
									/>
									{DATA_NEEDED_IMPORTANCE_LABELS[importance]}
								</SegmentedControls.Segment>
							))}
						</SegmentedControls>
					)}
				</PanelField>
			</div>

			<PanelField label="Linked factor" testId="request-linked-factor">
				{({ id, labelId }) =>
					editing ? (
						<div
							role="group"
							aria-labelledby={labelId}
							className="flex items-center justify-between gap-3 rounded-md bg-surface-light px-3 py-2"
						>
							<Text
								type="body"
								color={linkedFactor ? "primary" : "tertiary"}
								truncate
								className="min-w-0"
							>
								{draft.proConId
									? linkedFactor
										? `${linkedFactor.factor.name} (${linkedFactor.kind})`
										: "Linked factor"
									: "None"}
							</Text>
							{draft.proConId && (
								<ClickableText
									type="caption"
									color="link"
									testId="request-unlink-factor"
									onClick={() => patch({ proConId: null })}
								>
									Unlink
								</ClickableText>
							)}
						</div>
					) : (
						<DropdownInput
							buttonId={id}
							aria-labelledby={labelId}
							compact
							anchor="bottom start"
							optionsClassName="w-[var(--button-width)] max-w-[var(--button-width)] break-words"
							testId="request-linked-factor-select"
							value={draft.proConId ?? NO_FACTOR}
							options={factorOptions}
							onChange={(value: string) =>
								patch({
									proConId: linkableProCons.find((proCon) => proCon.id === value)?.id ?? null,
								})
							}
						/>
					)
				}
			</PanelField>
		</PanelSheet>
	);
};

export const RequestDataSheet: FC<{ sheet: RequestSheet }> = ({ sheet }) => {
	const { dataNeeded } = useDecisioningData();
	const { closeSheet } = useDecisioningPanel();
	const editId: DataNeededId | undefined = sheet.mode === "edit" ? sheet.dataNeededId : undefined;
	const item = editId ? dataNeeded.find((candidate) => candidate.id === editId) : undefined;
	const missing = editId !== undefined && item === undefined;

	useEffect(() => {
		if (missing) closeSheet();
	}, [missing, closeSheet]);

	if (missing) return null;

	return (
		<RequestSheetForm
			key={sheet.mode === "edit" ? sheet.dataNeededId : "add"}
			item={item}
			linkedProConId={sheet.mode === "add" ? sheet.linkedProConId : undefined}
			restoredDraft={sheet.draft}
		/>
	);
};
